
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { StoreModel } from '@/models/store';
import { get, put } from '@/services/http';
import debounce from 'lodash/debounce';
import { EventBus } from '@/eventBus';
import ImportCustomColumnValuesDialog from '@/components/settings/ImportCustomColumnValuesDialog.vue';

interface CustomColumn {
  id: string;
  label: string;
  enabled: boolean;
  order: number;
}

interface VariantRow {
  variantId: number;
  productId: number;
  productName: string;
  sku: string;
  variant: string;
  image: string;
}

interface CellValues {
  [variantId: string]: {
    [columnId: string]: string;
  };
}

@Component({
  components: {
    ImportCustomColumnValuesDialog
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    const vm = this as any;
    if (vm.hasChanges) {
      const answer = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  }
})
export default class CustomColumnValues extends Vue {
  @Prop({ type: Object, required: true }) store!: StoreModel;

  // Custom columns definition
  private customColumns: CustomColumn[] = [];

  // Variants data
  private variants: VariantRow[] = [];
  private totalItems = 0;

  // Values
  private originalValues: CellValues = {};
  private currentValues: CellValues = {};
  private modifiedCells: Set<string> = new Set();

  // UI State
  private loading = false;
  private saving = false;
  private searchQuery = '';
  private debouncedSearch: (() => void) | null = null;
  private showImportDialog = false;

  private tableOptions = {
    page: 1,
    itemsPerPage: 50,
    sortBy: [],
    sortDesc: []
  };

  // Computed
  get hasChanges(): boolean {
    return this.modifiedCells.size > 0;
  }

  get modifiedCellsCount(): number {
    return this.modifiedCells.size;
  }

  get headers() {
    const fixedHeaders = [
      { text: '', value: 'image', sortable: false, width: '60px' },
      { text: 'Product Name', value: 'productName', sortable: false, width: '200px' },
      { text: 'SKU', value: 'sku', sortable: false, width: '120px' },
      { text: 'Variant', value: 'variant', sortable: false, width: '120px' }
    ];

    const customHeaders = this.customColumns
      .filter(col => col.enabled)
      .sort((a, b) => a.order - b.order)
      .map(col => ({
        text: col.label,
        value: col.id,
        sortable: false,
        width: '150px'
      }));

    return [...fixedHeaders, ...customHeaders];
  }

  // Lifecycle
  created() {
    this.debouncedSearch = debounce(this.fetchData, 300);
  }

  async mounted() {
    await this.fetchCustomColumns();
    if (this.customColumns.length > 0) {
      await this.fetchData();
    }

    // Highlight column if specified in query parameter
    const raw = this.$route.query.column;
    const columnId = Array.isArray(raw) ? raw[0] : raw;
    if (typeof columnId === 'string' && columnId) {
      this.$nextTick(() => {
        this.highlightColumn(columnId);
      });
    }
  }

  highlightColumn(columnId: string) {
    // Find the column header element and scroll to it
    const label = this.getColumnLabel(columnId).trim();
    if (!label) return;

    const root = this.$el as HTMLElement;
    const headerCells = root.querySelectorAll('.custom-values-table th');
    const headerCell = Array.from(headerCells).find((el) =>
      (el.textContent || '').trim().includes(label)
    ) as HTMLElement | undefined;
    
    if (headerCell) {
      headerCell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      headerCell.classList.add('highlight-column');
      setTimeout(() => headerCell.classList.remove('highlight-column'), 2000);
    }
  }

  getColumnLabel(columnId: string): string {
    const column = this.customColumns.find(c => c.id === columnId);
    return column?.label || '';
  }

  // Methods
  async fetchCustomColumns() {
    try {
      const response: any = await get('/purchase_orders/config/custom-columns');
      const data = response.data;

      this.customColumns = Object.entries(data.columns || {})
        .map(([id, col]: [string, any]) => ({
          id,
          label: col.label,
          enabled: col.enabled,
          order: col.order
        }))
        .filter(col => col.enabled)
        .sort((a, b) => a.order - b.order);
    } catch (error) {
      console.error('Failed to load custom columns:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to load custom columns', color: 'error' });
    }
  }

  async fetchData() {
    this.loading = true;
    try {
      const { page, itemsPerPage } = this.tableOptions;

      // Build params for MasterData API
      const params: any = {
        page,
        limit: itemsPerPage,
        columns: 'variant_id,product_id,Item[Item Description],variant,product_sku,variant_image'
      };

      // Add search filter if provided - search across product name, sku, and variant
      if (this.searchQuery && this.searchQuery.trim()) {
        const searchValue = this.searchQuery.trim();
        // Use column_filter - search in product name (OLAP uses contains operator with LIKE)
        // Note: For multi-field search, we filter on Item Description which is the main product field
        params.column_filter = JSON.stringify([
          {
            code: 'Item[Item Description]',
            operator: 'contains',
            value: searchValue,
            dataType: 'text'
          }
        ]);
      }

      // Fetch variants from MasterData
      const variantsResponse: any = await get('/master-data/variants', params);

      // Parse OLAP response
      const variantsData = this.parseOlapResponse(variantsResponse);

      // Update variants list (reassign to ensure reactivity)
      this.variants = [...variantsData.rows];
      this.totalItems = variantsData.total;

      // Fetch custom column values for these variants
      const variantIds = this.variants.map(v => v.variantId);
      if (variantIds.length > 0) {
        await this.fetchCustomValues(variantIds);
      } else {
        // Clear values when no variants found
        this.originalValues = {};
        this.currentValues = {};
        this.modifiedCells = new Set();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to load data', color: 'error' });
    } finally {
      this.loading = false;
    }
  }

  async fetchCustomValues(variantIds: number[]) {
    try {
      const response: any = await get('/purchase_orders/config/custom-column-values', {
        object_ids: variantIds.join(','),
        object_type: 'variant'
      });

      // Build values map from response
      const valuesMap = response.data || {};

      // Reset values for current page
      this.originalValues = {};
      this.currentValues = {};

      // Initialize all variants with empty values
      variantIds.forEach(variantId => {
        const values = valuesMap[variantId] || {};
        this.originalValues[variantId] = { ...values };
        this.currentValues[variantId] = { ...values };
      });

      // Clear modified cells for the new page (reassign for Vue 2 reactivity)
      this.modifiedCells = new Set();
    } catch (error) {
      console.error('Failed to load custom values:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to load custom column values', color: 'error' });
    }
  }

  parseOlapResponse(response: any): { rows: VariantRow[], total: number } {
    const data = response?.data;

    // Check if it's OLAP format
    if (data?.columns && data?.rows) {
      const columns = data.columns || [];
      const rows = data.rows || [];
      // Total can be in pagination.total, data.total, or fallback to rows.length
      const total = data.pagination?.total || data.total || rows.length;

      const columnIndex: { [key: string]: number } = {};
      columns.forEach((col: any, index: number) => {
        columnIndex[col.code] = index;
      });

      const parsedRows: VariantRow[] = rows.map((row: any) => {
        const items = row.items || [];
        const getValue = (code: string) => {
          const idx = columnIndex[code];
          return idx !== undefined ? items[idx]?.value : undefined;
        };

        return {
          variantId: parseInt(getValue('variant_id') || '0', 10),
          productId: parseInt(getValue('product_id') || '0', 10),
          productName: getValue('Item[Item Description]') || getValue('product_name') || '',
          sku: getValue('product_sku') || getValue('sku') || '',
          variant: getValue('variant') || getValue('variant_title') || '',
          image: getValue('variant_image') || getValue('product_image') || ''
        };
      });

      return { rows: parsedRows, total };
    }

    // Fallback for direct array format
    if (Array.isArray(data)) {
      const parsedRows: VariantRow[] = data.map((item: any) => ({
        variantId: item.variant_id || item.variantId || 0,
        productId: item.product_id || item.productId || 0,
        productName: item.product_name || item.productName || '',
        sku: item.product_sku || item.sku || '',
        variant: item.variant || item.variant_title || '',
        image: item.variant_image || item.product_image || ''
      }));
      return { rows: parsedRows, total: parsedRows.length };
    }

    return { rows: [], total: 0 };
  }

  getCellValue(variantId: number, columnId: string): string {
    return this.currentValues[variantId]?.[columnId] || '';
  }

  isCellModified(variantId: number, columnId: string): boolean {
    return this.modifiedCells.has(`${variantId}:${columnId}`);
  }

  onCellChange(variantId: number, columnId: string, value: string) {
    if (!this.currentValues[variantId]) {
      this.currentValues[variantId] = {};
    }

    this.currentValues[variantId][columnId] = value;

    // Track modification
    const key = `${variantId}:${columnId}`;
    const originalValue = this.originalValues[variantId]?.[columnId] || '';

    if (value !== originalValue) {
      this.modifiedCells.add(key);
    } else {
      this.modifiedCells.delete(key);
    }

    // Force reactivity
    this.modifiedCells = new Set(this.modifiedCells);
  }

  async saveChanges() {
    if (!this.hasChanges) return;

    this.saving = true;
    try {
      // Build values object from currentValues that differ from originalValues
      // This is more reliable than tracking modifiedCells with Vue 2 reactivity
      const values: { [variantId: string]: { [columnId: string]: string } } = {};

      // Compare currentValues with originalValues to find changes
      Object.entries(this.currentValues).forEach(([variantIdStr, columnValues]) => {
        const variantId = parseInt(variantIdStr, 10);
        const originalColumnValues = this.originalValues[variantId] || {};

        Object.entries(columnValues).forEach(([columnId, value]) => {
          const originalValue = originalColumnValues[columnId] || '';
          if (value !== originalValue) {
            if (!values[variantIdStr]) {
              values[variantIdStr] = {};
            }
            values[variantIdStr][columnId] = value;
          }
        });
      });

      if (Object.keys(values).length === 0) {
        EventBus.$emit('show-snackbar', { text: 'No changes to save', color: 'info' });
        return;
      }

      await put('/purchase_orders/config/custom-column-values', {
        object_type: 'variant',
        values
      });

      // Update original values
      Object.entries(values).forEach(([variantId, columnValues]) => {
        const vid = parseInt(variantId, 10);
        if (!this.originalValues[vid]) {
          this.originalValues[vid] = {};
        }
        Object.assign(this.originalValues[vid], columnValues);
      });

      // Clear and reassign to trigger Vue 2 reactivity
      this.modifiedCells = new Set();
      EventBus.$emit('show-snackbar', { text: 'Changes saved successfully', color: 'success' });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save changes';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.saving = false;
    }
  }

  discardChanges() {
    // Restore original values
    this.currentValues = {};
    Object.entries(this.originalValues).forEach(([variantId, values]) => {
      this.currentValues[parseInt(variantId, 10)] = { ...values };
    });

    // Reassign to trigger Vue 2 reactivity
    this.modifiedCells = new Set();
  }

  onOptionsChange() {
    if (this.customColumns.length > 0) {
      this.fetchData();
    }
  }

  onSearchInput() {
    // Reset to page 1 when searching
    this.tableOptions.page = 1;
    if (this.debouncedSearch) {
      this.debouncedSearch();
    }
  }

  onSearchClear() {
    this.searchQuery = '';
    // Reset to page 1 when clearing search
    this.tableOptions.page = 1;
    this.fetchData();
  }

  onImportSuccess() {
    // Refresh the data after successful import
    this.fetchData();
    EventBus.$emit('show-snackbar', { text: 'Import completed successfully', color: 'success' });
  }
}
